import React, { Component } from 'react';
import styled from 'styled-components';

import Input from '../../components/Input';
import JsonLogicBuilder from '../../components/JsonLogicBuilder';
import Notification from '../../components/Notification';

import { getLogicBuilderVariables } from './shared';
import DeletableCard from './DeletableCard';
import SubConfiguration from './SubConfiguration';
import { areShallowEqual } from '../../lib/helpers';
import AlertDialog from "../../components/AlertDialog";
import Button from "../../components/Button";

class ContextAlertBuilder extends Component {
  shouldComponentUpdate(prevProps, prevState) {
    const { contextAlert, validations } = this.props;

    if (prevState !== this.state) {
      // Always update when the local state changes.
      return true;
    }

    if (prevProps.contextAlert !== contextAlert) {
      // Always update when the contextAlert changes.
      return true;
    }

    if (!areShallowEqual(prevProps.validations, validations)) {
      // Always update when the validations have changed.
      return true;
    }

    return false;
  }

  findFormById = (formId) => {
    return this.props.forms.find((form) => form.id === formId) || null;
  };

  state = {
    selectedFormId: this.props.contextAlert.formId || "",
    dialog: null,
  };

  setContextAlertState = reducer => {
    const { contextAlert, onChange } = this.props;

    onChange({
      ...contextAlert,
      ...(typeof reducer === 'function' ? reducer(contextAlert) : reducer)
    });
  };

  handleDeleteContextAlert = () => {
    const { contextAlert, onRemove } = this.props;
    onRemove(contextAlert);
  };

  handleContextAlertNameChanged = e => {
    const { contextAlert } = this.props;

    if (contextAlert.name !== e.target.value) {
      this.setContextAlertState({ name: e.target.value, description: e.target.value });
    }
  };

  handleIsRequiredChanged = e => {
    const { contextAlert } = this.props;
    const isChecked = e.target.checked;
  
    if (contextAlert.required !== isChecked) {
      this.setContextAlertState({ required: isChecked });
    }
  };

  handleContextAlertDescriptionChanged = e => {
    const { contextAlert } = this.props;

    if (contextAlert.description !== e.target.value) {
      this.setContextAlertState({ description: e.target.value });
    }
  };

  closeAlertDialog = () => {
    this.setState({dialog: null});
  }

  openAlertDialog = props => {
    this.setState({dialog: props});
  }

  handleFormSelectionChanged = (e) => {
    const selectedFormId = e.target.value;
    const {forms} = this.props;
    const form = forms.find(f => f.id === selectedFormId); //grab the form based on the selected form id
    this.setState({ selectedFormId });
    this.setContextAlertState({ formId: selectedFormId });
    if(form && form.groups.length !== 0){ // if there are groups associated with the form, open the alert dialog
      this.openAlertDialog({
        title: "Launch Form Restricted to User Groups",
        description: `The selected launch form has user group restrictions.\nEnsure template users have access to the groups: "${form.groups.map(g => g.name).join(", ")}".`,
        extraButtons: [
          <Button key ="to_user_groups" onClick={()=> {window.open("/users/groups","_blank"); this.closeAlertDialog()}}>Manage User Groups</Button>,
        ]
      });
    }
  };

  handleExpressionChanged = expression => {
    this.setContextAlertState({expression: JSON.stringify(expression)});
  }

  handleChanged = expression => {
    this.setContextAlertState({expression: JSON.stringify(expression)});
  }
    
  render() {
    const {form, catalog, contextAlert, validations, forms} = this.props;

    return (
      <DeletableCard
        focusable
        dialogTitle="Delete this launch alert?"
        dialogDescription="Are you sure you want to delete this launch alert? This cannot be undone."
        ok="Delete"
        onOk={this.handleDeleteContextAlert}
        overlay={this.state.dialog && <AlertDialog onOk={this.closeAlertDialog} {...this.state.dialog} />}>
        <ContextAlertForm>
          <Input
            id={`contextAlert_title_${contextAlert.id}`}
            label="Launch alert name"
            placeholder="Enter a short name for this launch alert"
            defaultValue={contextAlert.name}
            onBlur={this.handleContextAlertNameChanged}
            invalid={validations.name}
            validationText={validations.name}
          />
          {validations.expression && (
            <Notification mini error>{validations.expression}</Notification>
          )}
          <Input
            id={`form_link_select_${contextAlert.id}`}
            label="Take the user to ..."
            type="select"
            value={contextAlert.formId}
            onChange={this.handleFormSelectionChanged}
          >
            <option value="">Select a form</option>
            {forms.slice().sort((a, b) => a.name.localeCompare(b.name)).map((form) => (
              <option key={form.id} value={form.id}>
                {form.name}
              </option>
            ))}
          </Input>


          <Condition>
            <label>Trigger this form launch alert when...</label>
            <SubConfiguration>
              <SubConfiguration.Item>
                <JsonLogicBuilder
                  expand
                  variables={getLogicBuilderVariables(form, catalog)}
                  value={JSON.parse(contextAlert.expression)}
                  onChange={this.handleExpressionChanged}
                />
              </SubConfiguration.Item>
            </SubConfiguration>
          </Condition>
        </ContextAlertForm>
      </DeletableCard>
    );
  }
}
  
const ContextAlertForm = styled.div`
> * {
  margin-bottom: 1.5rem;

  :last-child {
    margin-bottom: 0;
  }
}
`;

const Condition = styled.div`
  label {
    display: block;
    margin-bottom: 0.5rem;
  }
`;

export default ContextAlertBuilder;

